import React, { useCallback, useEffect } from 'react';

import SelectableChip from 'retired/SelectableChip';
import {
  ChallengeStatus,
  ChallengesQueryVariables,
  QueryMode,
  ChallengeWhereInput,
} from 'generated/graphql';

const DEBOUNCE_DELAY = 300;
const MIN_SEARCH_LENGTH = 3;
const now = new Date();

export type FilterStatus = ChallengeStatus | 'All' | 'Past' | 'Upcoming';

const buildSearchQuery = (
  searchValue: string,
  filterStatus: FilterStatus
): { where?: ChallengeWhereInput } => {
  let baseQuery: ChallengeWhereInput = {};

  if (filterStatus === 'Past') {
    baseQuery = {
      endDate: { lt: now },
    };
  } else if (filterStatus === 'Upcoming') {
    baseQuery = {
      startDate: { gte: now },
    };
  } else if (filterStatus !== 'All' && filterStatus) {
    baseQuery = {
      status: { equals: filterStatus },
    };
  }

  if (searchValue.length < MIN_SEARCH_LENGTH) {
    return { where: baseQuery };
  }

  return {
    where: {
      ...baseQuery,
      title: { contains: searchValue, mode: QueryMode.Insensitive },
    },
  };
};

const options: { status: FilterStatus; label: string }[] = [
  { status: 'All', label: 'All' },
  { status: 'Past', label: 'Past' },
  { status: 'Upcoming', label: 'Upcoming' },
  { status: ChallengeStatus.Draft, label: 'Draft' },
  { status: ChallengeStatus.Published, label: 'Published' },
];

interface FiltersProps {
  setQueryVariables: (variables: ChallengesQueryVariables) => void;
  defaultQueryVariables: ChallengesQueryVariables;
  searchValue: string;
  selectedFilterStatus: FilterStatus;
  setSelectedFilterStatus: (status: FilterStatus) => void;
}

export const Filters = ({
  setQueryVariables,
  defaultQueryVariables,
  searchValue,
  selectedFilterStatus,
  setSelectedFilterStatus,
}: FiltersProps): JSX.Element => {
  const updateQueryVariables = useCallback(
    (search: string, status: FilterStatus, resetPagination = false) => {
      const searchQuery = buildSearchQuery(search, status);
      setQueryVariables({
        ...defaultQueryVariables,
        ...searchQuery,
        ...(resetPagination ? { skip: 0 } : {}),
      });
    },
    [defaultQueryVariables, setQueryVariables]
  );

  const debouncedSearch = useCallback(
    (value: string) => {
      updateQueryVariables(value, selectedFilterStatus);
    },
    [selectedFilterStatus, updateQueryVariables]
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      debouncedSearch(searchValue);
    }, DEBOUNCE_DELAY);

    return () => {
      clearTimeout(timer);
    };
  }, [searchValue, debouncedSearch]);

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          marginBottom: '16px',
        }}
      >
        <div style={{ marginBottom: -8 }}>
          {options.map((option) => (
            <SelectableChip
              key={option.status || 'all'}
              label={option.label}
              onClick={(): void => {
                setSelectedFilterStatus(option.status);
                updateQueryVariables(searchValue, option.status, true);
              }}
              selected={selectedFilterStatus === option.status}
            />
          ))}
        </div>
      </div>
    </>
  );
};

export default Filters;
